import React, { useState, memo, useMemo, useCallback } from "react";

import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import Drawer from "@material-ui/core/Drawer";
import List from "@material-ui/core/List";
import CssBaseline from "@material-ui/core/CssBaseline";
import IconButton from "@material-ui/core/IconButton";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import {
  Dehaze,
  Close,
  Home,
  LocalShipping,
  Settings,
  ExpandLess,
  ExpandMore,
  FormatListBulleted,
  Person,
  DashboardOutlined,
  ExitToApp,
  PhoneIphone,
  Business,
  Speed,
  Cake,
  Search,
  BarChart,
  ShowChart,
  LineStyleRounded,
  Receipt,
  PeopleOutline,
  LocalShippingOutlined,
  WhatsApp,
  Map,
  Storefront,
  TimelineOutlined,
  Public,
  LocationOn,
  Print,
} from "@material-ui/icons";
import { Box, Collapse, Hidden } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { signOut } from "../../store/modules/Auth/actions";
import { IconGoal, IconPackage, Santolabs } from "../Custom";
import ShoppingBasketIcon from "@material-ui/icons/ShoppingBasket";
import AddShoppingCartIcon from "@material-ui/icons/AddShoppingCart";
import EmojiObjectsIcon from "@material-ui/icons/EmojiObjects";
import iconPascoa from "../../Assets/img/feliz-pascoa.png";
import ContactPhoneIcon from "@material-ui/icons/ContactPhone";
import PermPhoneMsgIcon from "@material-ui/icons/PermPhoneMsg";
import ContactsIcon from "@material-ui/icons/Contacts";
import BookmarkBorderIcon from "@material-ui/icons/BookmarkBorder";
import BookmarksIcon from "@material-ui/icons/Bookmarks";
import BookIcon from "@material-ui/icons/Book";
import DoubleArrowOutlinedIcon from "@material-ui/icons/DoubleArrowOutlined";
import CallSplitIcon from "@material-ui/icons/CallSplit";
import KeyboardReturnIcon from "@material-ui/icons/KeyboardReturn";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import HistoryIcon from "@material-ui/icons/History";
import ReceiptOutlined from "@material-ui/icons/ReceiptOutlined";

const drawerWidth = 300;

// Normaliza profile.menus (string "1,2,3", array, ou null) para um Set de números.
// Esses IDs correspondem ao idPage das rotas / menu_id usado em valid_key.
function normalizeUserMenus(profile) {
  if (!profile || profile.menus == null) return new Set();
  const raw = profile.menus;
  const list = Array.isArray(raw)
    ? raw
    : typeof raw === "string"
      ? raw.split(",")
      : [];
  return new Set(
    list.map((v) => Number(String(v).trim())).filter((n) => !Number.isNaN(n))
  );
}

// Mapa de cada seção/subseção do menu para as rotas dos seus filhos.
// Usado para destacar o menu pai ativo e auto-expandir a seção da página atual.
const MENU_SECTIONS = {
  santolog: {
    sub: {
      santologLogistica: [
        "/santolog/home",
        "/santolog/separacao",
        "/santolog/embarcar",
        "/santolog/romaneio",
        "/santolog/transferencia",
        "/santolog/devolucao",
      ],
      santologMapa: ["/santolog/mapa", "/santolog/areas"],
      santologCadastros: [
        "/santolog/pedidos",
        "/santolog/boxes",
        "/santolog/motoristas",
        "/santolog/transportadoras",
      ],
      santologAuditoria: ["/santolog/auditoria"],
      santologFechamentoFrete: ["/santolog/fechamento-frete"],
    },
  },
  telesales: { hrefs: ["/consulta-margem"] },
  atendimento: { hrefs: ["/atendimento/novo"] },
  cascas: { hrefs: ["/cascas/adicionar-cascas", "/cascas/separar-cascas-v2"] },
  settings: {
    hrefs: [
      "/configuracao/metas-gerais",
      "/configuracao/menu",
      "/configuracao/usuarios",
    ],
  },
  dashboard: {
    hrefs: [
      "/dashboard/carrinho-abandonado",
      "/dashboard/metas-gerais",
      "/dashboard/quantidade-de-pedidos",
      "/dashboard/cascas",
      "/dashboard/sac-v2",
      "/dashboard/televendas",
    ],
  },
  loja: { hrefs: ["/dashboard/ranking-caixas", "/dashboard/produtividade"] },
  site: {
    hrefs: [
      "/dashboard/conciliacao-frete",
      "/dashboard/ciclo",
      "/dashboard/embarque",
      "/dash/exp",
      "/dashboard/tracking",
      "/dashboard/gerar-etiquetas",
      "/dashboard/metas/expedicao",
      "/dashboard/transportadoras",
    ],
    sub: {
      integracao: ["/integracao/embarque"],
      ondas: ["/dashboard/ondas"],
    },
  },
  ebook: { hrefs: ["/ebook/novo", "/ebook/listar"] },
  embalei: {
    hrefs: [
      "/embalei/usuario",
      "/embalei/pesquisa",
      "/embalei/ranking",
      "/embalei/meta",
    ],
  },
  seals: { hrefs: ["/selos/adicionar", "/selos/ativar-desativar"] },
};

// Descobre qual seção (e subseção) contém a rota atual.
function findActiveSection(path) {
  for (const topKey of Object.keys(MENU_SECTIONS)) {
    const def = MENU_SECTIONS[topKey];
    if (def.hrefs && def.hrefs.includes(path)) return { top: topKey, sub: null };
    if (def.sub) {
      for (const subKey of Object.keys(def.sub)) {
        if (def.sub[subKey].includes(path)) return { top: topKey, sub: subKey };
      }
    }
  }
  return { top: null, sub: null };
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  menuButton: {
    marginRight: 36,
  },
  hide: {
    display: "none",
  },
  mainMarginSimulator: {
    // background: "rgb(19, 21, 26)",
    paddingTop: 35,
  },
  appName: {
    color: "#fff",
    position: "relative",
    right: 30,
    "& b": {
      color: "#e63246",
    },
  },

  drawer: {
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: "nowrap",

    // Tamanho de fonte dos itens (14px ⇒ 0.875rem)
    "& .MuiListItemText-primary": {
      fontSize: "0.875rem",
      letterSpacing: "0.01em",
    },

    "& .menu": {
      color: "#fff",
      marginBottom: 2,
      transition: "background-color .15s ease, box-shadow .15s ease",
      "& svg": {
        color: "#fff",
      },
      "& .MuiListItemText-primary": {
        fontWeight: 500,
      },

      "&:hover": {
        background: "rgba(255, 255, 255, 0.07)",
      },

      // Seção pai expandida (collapse aberto), quando não é a página atual
      "&.open": {
        background: "rgba(255, 255, 255, 0.05)",
      },
      "&.open .MuiListItemText-primary": {
        fontWeight: 600,
      },

      // Pai cuja página filha está ativa — destaca a trilha até a página
      "&.parent-active": {
        background: "#ff314a",
      },
      "&.parent-active .MuiListItemText-primary": {
        fontWeight: 700,
      },

      // Página atual (item folha)
      "&.active": {
        background: "#ff314a",
        boxShadow: "inset 4px 0 0 #fff",
      },
      "&.active .MuiListItemText-primary": {
        fontWeight: 700,
      },
    },

    // Hierarquia: filhos dentro de um collapse ganham guia vertical + recuo
    "& .MuiCollapse-root > .MuiList-root": {
      marginLeft: 22,
      borderLeft: "2px solid rgba(255, 255, 255, 0.12)",
    },
  },
  drawerOpen: {
    width: drawerWidth,
    [theme.breakpoints.down("sm")]: {
      width: "100% !important",
    },

    height: "100%",
    overflowY: "auto",
    overflowX: "hidden",
    WebkitOverflowScrolling: "touch",
    // Rolável, mas sem barra de scroll visível
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "&::-webkit-scrollbar": {
      display: "none",
      width: 0,
      height: 0,
    },
    border: "none",
    background: "#0c0f18 !important",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    "& .MuiList-root": {
      paddingLeft: 15,
      paddingRight: 15,
    },
    "& .menu": {
      borderRadius: 6,
    },
    // Aberto: logo à esquerda + X à direita
    "& $toolbar": {
      justifyContent: "space-between",
    },
  },
  drawerClose: {
    height: "100%",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    // Rolável, mas sem barra de scroll visível
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "&::-webkit-scrollbar": {
      display: "none",
      width: 0,
      height: 0,
    },
    border: "none",
    background: "#0c0f18 !important",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),

    "& .MuiList-root": {
      paddingLeft: 0,
      paddingRight: 0,
    },

    overflowX: "hidden",

    width: theme.spacing(7) + 1,
    [theme.breakpoints.down("sm")]: {
      width: "0 !important",
    },
    // Fechado: hambúrguer centralizado
    "& $toolbar": {
      justifyContent: "center",
    },
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    // Aberto = space-between (logo + X); fechado = center (só o hambúrguer).
    // O justify-content é definido por estado em drawerOpen/drawerClose.
    justifyContent: "center",
    padding: "10px 20px",
    "& button": {
      color: "#fff",
      padding: "0px !important",
    },
  },
  toolbarLogo: {
    paddingLeft: 12,
    whiteSpace: "nowrap",
    lineHeight: 1,
  },
  content: {
    flexGrow: 1,
    minHeight: "100vh",
  },

  iconPascoa: {
    width: 26,
    minWidth: 50,
    "& img": {
      width: "50%",
      display: "block",
    },
  },
}));

function Menu({
  menu,
  subMenu,
  setMenu,
  setSubMenu,
  openMenu,
  openSubMenu,
  setOpenMenu,
  setOpenSubMenu,
  classes,
  title,
  signOutlogin,
  can,
  activeTop,
  activeSub,
}) {
  // Visibilidade das seções: aparecem se o usuário tiver ao menos um filho liberado.
  const showSantologLogistica =
    can(38) || can(50) || can(49) || can(54) || can(52) || can(51);
  const showSantologMapa = can(37) || can(40);
  const showSantologCadastros = can(47) || can(48) || can(43) || can(44);
  const showSantolog =
    showSantologLogistica || showSantologMapa || showSantologCadastros ||
    can(56) || can(62);
  const showTelevendas = can(35);
  const showAtendimento = can(16);
  const showCascas = can(11) || can(13);
  const showSettings = can(23) || can(3) || can(4);
  const showDashboards =
    can(8) || can(22) || can(9) || can(14) || can(24) || can(27);
  const showLoja = can(41) || can(42);
  // Site contém links públicos (Expedição, Embarque, Ciclo) → sempre visível
  const showSite = true;
  const showEbook = can(20) || can(21);
  const showEmbalei = can(29) || can(17) || can(18) || can(19);
  const showSelos = can(30) || can(31);

  return (
    <>
      <div className={classes.toolbar}>
        {openMenu && (
          <Santolabs className={classes.toolbarLogo}>
            <span>santo</span>
            <span>Labs</span>
            <span>^</span>
          </Santolabs>
        )}
        <IconButton onClick={() => setOpenMenu(!openMenu)}>
          {openMenu ? <Close /> : <Dehaze />}
        </IconButton>
      </div>

      <List>
        <ListItem
          component="a"
          href="/home"
          className={`menu ${title === "Início" && "active"}`}
        >
          <ListItemIcon>
            {" "}
            <Home />{" "}
          </ListItemIcon>
          <ListItemText primary={"Início"} />
        </ListItem>

        {showSantolog && (
          <>
            <ListItem
              button
              onClick={(e) => {
                setMenu({ santolog: !menu.santolog });
                if (!openMenu) setOpenMenu(!openMenu);
              }}
              className={`menu ${menu.santolog ? "open" : ""} ${activeTop === "santolog" ? "parent-active" : ""
                }`}
            >
              <ListItemIcon>
                <LocalShipping />
              </ListItemIcon>
              <ListItemText primary="SantoLog" />
              {menu.santolog ? <ExpandLess /> : <ExpandMore />}
            </ListItem>

            <Collapse in={openMenu && menu.santolog} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>

                {/* ── LOGÍSTICA ─────────────────────────────────────── */}
                {showSantologLogistica && (
                  <>
                    <ListItem
                      button
                      onClick={(e) => {
                        setSubMenu({ ...subMenu, santologLogistica: !subMenu.santologLogistica });
                        if (!openSubMenu) setOpenSubMenu(!openSubMenu);
                      }}
                      className={`menu ${subMenu.santologLogistica ? "open" : ""} ${activeSub === "santologLogistica" ? "parent-active" : ""
                        }`}
                    >
                      <ListItemIcon>
                        <LocalShippingOutlined />
                      </ListItemIcon>
                      <ListItemText primary="Logística" />
                      {subMenu.santologLogistica ? <ExpandLess /> : <ExpandMore />}
                    </ListItem>

                    <Collapse in={openMenu && subMenu.santologLogistica} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {can(38) && (
                          <ListItem
                            component="a"
                            href="/santolog/home"
                            className={`menu ${title === "Monta Carga" && "active"}`}
                          >
                            <ListItemIcon>
                              <Home />
                            </ListItemIcon>
                            <ListItemText primary="Monta Carga" />
                          </ListItem>
                        )}
                        {can(50) && (
                          <ListItem
                            component="a"
                            href="/santolog/separacao"
                            className={`menu ${title === "Separação" && "active"}`}
                          >
                            <ListItemIcon>
                              <CallSplitIcon />
                            </ListItemIcon>
                            <ListItemText primary="Separação" />
                          </ListItem>
                        )}
                        {can(49) && (
                          <ListItem
                            component="a"
                            href="/santolog/embarcar"
                            className={`menu ${title === "Embarcar" && "active"}`}
                          >
                            <ListItemIcon>
                              <DoubleArrowOutlinedIcon />
                            </ListItemIcon>
                            <ListItemText primary="Embarcar" />
                          </ListItem>
                        )}
                        {can(54) && (
                          <ListItem
                            component="a"
                            href="/santolog/romaneio"
                            className={`menu ${title === "Romaneio" && "active"}`}
                          >
                            <ListItemIcon>
                              <Print />
                            </ListItemIcon>
                            <ListItemText primary="Romaneio" />
                          </ListItem>
                        )}
                        {can(52) && (
                          <ListItem
                            component="a"
                            href="/santolog/transferencia"
                            className={`menu ${title === "Transferência" && "active"}`}
                          >
                            <ListItemIcon>
                              <SwapHorizIcon />
                            </ListItemIcon>
                            <ListItemText primary="Transferência" />
                          </ListItem>
                        )}
                        {can(51) && (
                          <ListItem
                            component="a"
                            href="/santolog/devolucao"
                            className={`menu ${title === "Devolução" && "active"}`}
                          >
                            <ListItemIcon>
                              <KeyboardReturnIcon />
                            </ListItemIcon>
                            <ListItemText primary="Devolução" />
                          </ListItem>
                        )}
                      </List>
                    </Collapse>
                  </>
                )}

                {/* ── MAPA ──────────────────────────────────────────── */}
                {showSantologMapa && (
                  <>
                    <ListItem
                      button
                      onClick={(e) => {
                        setSubMenu({ ...subMenu, santologMapa: !subMenu.santologMapa });
                        if (!openSubMenu) setOpenSubMenu(!openSubMenu);
                      }}
                      className={`menu ${subMenu.santologMapa ? "open" : ""} ${activeSub === "santologMapa" ? "parent-active" : ""
                        }`}
                    >
                      <ListItemIcon>
                        <Map />
                      </ListItemIcon>
                      <ListItemText primary="Mapa" />
                      {subMenu.santologMapa ? <ExpandLess /> : <ExpandMore />}
                    </ListItem>

                    <Collapse in={openMenu && subMenu.santologMapa} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {can(37) && (
                          <ListItem
                            component="a"
                            href="/santolog/mapa"
                            className={`menu ${title === "Mapa" && "active"}`}
                          >
                            <ListItemIcon>
                              <Map />
                            </ListItemIcon>
                            <ListItemText primary="Pedidos" />
                          </ListItem>
                        )}
                        {can(40) && (
                          <ListItem
                            component="a"
                            href="/santolog/areas"
                            className={`menu ${title === "Áreas" && "active"}`}
                          >
                            <ListItemIcon>
                              <Map />
                            </ListItemIcon>
                            <ListItemText primary="Áreas" />
                          </ListItem>
                        )}
                      </List>
                    </Collapse>
                  </>
                )}

                {/* ── CADASTROS ─────────────────────────────────────── */}
                {showSantologCadastros && (
                  <>
                    <ListItem
                      button
                      onClick={(e) => {
                        setSubMenu({ ...subMenu, santologCadastros: !subMenu.santologCadastros });
                        if (!openSubMenu) setOpenSubMenu(!openSubMenu);
                      }}
                      className={`menu ${subMenu.santologCadastros ? "open" : ""} ${activeSub === "santologCadastros" ? "parent-active" : ""
                        }`}
                    >
                      <ListItemIcon>
                        <FormatListBulleted />
                      </ListItemIcon>
                      <ListItemText primary="Cadastros" />
                      {subMenu.santologCadastros ? <ExpandLess /> : <ExpandMore />}
                    </ListItem>

                    <Collapse in={openMenu && subMenu.santologCadastros} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {can(47) && (
                          <ListItem
                            component="a"
                            href="/santolog/pedidos"
                            className={`menu ${title === "Pedidos" && "active"}`}
                          >
                            <ListItemIcon>
                              <Receipt />
                            </ListItemIcon>
                            <ListItemText primary="Pedidos" />
                          </ListItem>
                        )}
                        {can(48) && (
                          <ListItem
                            component="a"
                            href="/santolog/boxes"
                            className={`menu ${title === "Boxes" && "active"}`}
                          >
                            <ListItemIcon>
                              <ShoppingBasketIcon />
                            </ListItemIcon>
                            <ListItemText primary="Boxes" />
                          </ListItem>
                        )}
                        {can(43) && (
                          <ListItem
                            component="a"
                            href="/santolog/motoristas"
                            className={`menu ${title === "Motoristas" && "active"}`}
                          >
                            <ListItemIcon>
                              <PeopleOutline />
                            </ListItemIcon>
                            <ListItemText primary="Motoristas" />
                          </ListItem>
                        )}
                        {can(44) && (
                          <ListItem
                            component="a"
                            href="/santolog/transportadoras"
                            className={`menu ${title === "Transportadoras" && "active"}`}
                          >
                            <ListItemIcon>
                              <Business />
                            </ListItemIcon>
                            <ListItemText primary="Transportadoras" />
                          </ListItem>
                        )}
                      </List>
                    </Collapse>
                  </>
                )}

                {/* Auditoria — item root dentro do SantoLog */}
                {can(56) && (
                  <ListItem
                    component="a"
                    href="/santolog/auditoria"
                    className={`menu ${title === "Auditoria" && "active"}`}
                  >
                    <ListItemIcon>
                      <HistoryIcon />
                    </ListItemIcon>
                    <ListItemText primary="Auditoria" />
                  </ListItem>
                )}

                {/* Fechamento Frete — item root dentro do SantoLog */}
                {can(62) && (
                  <ListItem
                    component="a"
                    href="/santolog/fechamento-frete"
                    className={`menu ${title === "Fechamento Frete" && "active"}`}
                  >
                    <ListItemIcon>
                      <ReceiptOutlined />
                    </ListItemIcon>
                    <ListItemText primary="Fechamento Frete" />
                  </ListItem>
                )}

              </List>
            </Collapse>
          </>
        )}

        <ListItem
          button
          onClick={(e) => {
            setMenu({ apps: !menu.apps });
            if (!openMenu) setOpenMenu(!openMenu);
          }}
          className={`menu`}
        >
          <ListItemIcon>
            <PhoneIphone />
          </ListItemIcon>
          <ListItemText primary="Aplicações" />
          {menu.apps ? <ExpandLess /> : <ExpandMore />}
        </ListItem>

        <Collapse in={openMenu && menu.apps} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItem
              component="a"
              href="http://www.intranet.santolabs.com.br"
              target="_blank"
              className={`menu ${title === "Intranet" && "active"}`}
            >
              <ListItemIcon>
                <Business />
              </ListItemIcon>
              <ListItemText primary="Intranet" />
            </ListItem>

            <ListItem
              component="a"
              href="http://developer.romulopavan.com.br/"
              target="_blank"
              className={`menu ${title === "Separa" && "active"}`}
            >
              <ListItemIcon>
                <Speed />
              </ListItemIcon>
              <ListItemText primary="Separa" />
            </ListItem>

            <ListItem
              component="a"
              href="http://cursos.santolabs.com.br/"
              target="_blank"
              className={`menu ${title === "Cursos" && "active"}`}
            >
              <ListItemIcon>
                <Cake />
              </ListItemIcon>
              <ListItemText primary="Cursos" />
            </ListItem>
          </List>
        </Collapse>

        {showTelevendas && (
          <>
            <ListItem
              button
              onClick={(e) => {
                setMenu({ telesales: !menu.telesales });
                if (!openMenu) setOpenMenu(!openMenu);
              }}
              className={`menu ${menu.telesales ? "open" : ""} ${activeTop === "telesales" ? "parent-active" : ""
                }`}
            >
              <ListItemIcon>
                <WhatsApp />
              </ListItemIcon>
              <ListItemText primary="Televendas" />
              {menu.telesales ? <ExpandLess /> : <ExpandMore />}
            </ListItem>

            <Collapse in={openMenu && menu.telesales} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItem
                  component="a"
                  href="/consulta-margem"
                  className={`menu ${title === "Consulta Margem" && "active"}`}
                >
                  <ListItemIcon>
                    <Search />
                  </ListItemIcon>
                  <ListItemText primary="Consulta Margem" />
                </ListItem>
              </List>
            </Collapse>
          </>
        )}

        {showAtendimento && (
          <>
            <ListItem
              button
              onClick={(e) => {
                setMenu({ atendimento: !menu.atendimento });
                if (!openMenu) setOpenMenu(!openMenu);
              }}
              className={`menu ${menu.atendimento ? "open" : ""} ${activeTop === "atendimento" ? "parent-active" : ""
                }`}
            >
              <ListItemIcon>
                <ContactPhoneIcon />
              </ListItemIcon>
              <ListItemText primary="Atendimento" />
              {menu.atendimento ? <ExpandLess /> : <ExpandMore />}
            </ListItem>

            <Collapse in={openMenu && menu.atendimento} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItem
                  component="a"
                  href="/atendimento/novo"
                  className={`menu ${title === "Atendimento" && "active"}`}
                >
                  <ListItemIcon>
                    <PermPhoneMsgIcon />
                  </ListItemIcon>
                  <ListItemText primary={"Atendimento novo"} />
                </ListItem>
              </List>
            </Collapse>
          </>
        )}

        {showCascas && (
          <>
            <ListItem
              button
              onClick={(e) => {
                setMenu({ cascas: !menu.cascas });
                if (!openMenu) setOpenMenu(!openMenu);
              }}
              className={`menu ${menu.cascas ? "open" : ""} ${activeTop === "cascas" ? "parent-active" : ""
                }`}
            >
              <Box className={classes.iconPascoa}>
                <img src={iconPascoa} alt="Pascoa" />
              </Box>
              <ListItemText primary="Cascas" />
              {menu.separaCasas ? <ExpandLess /> : <ExpandMore />}
            </ListItem>

            <Collapse in={openMenu && menu.cascas} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {can(11) && (
                  <ListItem
                    component="a"
                    href="/cascas/adicionar-cascas"
                    className={`menu ${title === "Adicionar Cascas" && "active"}`}
                  >
                    <ListItemIcon>
                      {" "}
                      <EmojiObjectsIcon />{" "}
                    </ListItemIcon>
                    <ListItemText primary={"Adicionar Cascas"} />
                  </ListItem>
                )}
                {can(13) && (
                  <ListItem
                    component="a"
                    href="/cascas/separar-cascas-v2"
                    className={`menu ${title === "Separar Cascas" && "active"}`}
                  >
                    <ListItemIcon>
                      <ShoppingBasketIcon />
                    </ListItemIcon>
                    <ListItemText primary="Separar Cascas" />
                  </ListItem>
                )}
              </List>
            </Collapse>
          </>
        )}

        {showSettings && (
          <>
            <ListItem
              button
              onClick={(e) => {
                setMenu({ settings: !menu.settings });
                if (!openMenu) setOpenMenu(!openMenu);
              }}
              className={`menu ${menu.settings ? "open" : ""} ${activeTop === "settings" ? "parent-active" : ""
                }`}
            >
              <ListItemIcon>
                <Settings />
              </ListItemIcon>
              <ListItemText primary="Configurações" />
              {menu.settings ? <ExpandLess /> : <ExpandMore />}
            </ListItem>

            <Collapse in={openMenu && menu.settings} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {can(23) && (
                  <ListItem
                    component="a"
                    href="/configuracao/metas-gerais"
                    className={`menu ${title === "Configuração Metas Gerais" && "active"
                      }`}
                  >
                    <ListItemIcon>
                      <ShowChart />
                    </ListItemIcon>
                    <ListItemText primary="Metas Gerais" />
                  </ListItem>
                )}

                {can(3) && (
                  <ListItem
                    component="a"
                    href="/configuracao/menu"
                    className={`menu ${title === "Menu" && "active"}`}
                  >
                    <ListItemIcon>
                      <FormatListBulleted />
                    </ListItemIcon>
                    <ListItemText primary="Menu" />
                  </ListItem>
                )}

                {can(4) && (
                  <ListItem
                    component="a"
                    href="/configuracao/usuarios"
                    className={`menu ${title === "Usuários" && "active"}`}
                  >
                    <ListItemIcon>
                      <Person />
                    </ListItemIcon>
                    <ListItemText primary="Usuários" />
                  </ListItem>
                )}
              </List>
            </Collapse>
          </>
        )}

        {showDashboards && (
          <>
            <ListItem
              button
              onClick={(e) => {
                setMenu({ dashboard: !menu.dashboard });
                if (!openMenu) setOpenMenu(!openMenu);
              }}
              className={`menu ${menu.dashboard ? "open" : ""} ${activeTop === "dashboard" ? "parent-active" : ""
                }`}
            >
              <ListItemIcon>
                <DashboardOutlined />
              </ListItemIcon>
              <ListItemText primary="Dashboards" />
              {menu.dashboard ? <ExpandLess /> : <ExpandMore />}
            </ListItem>

            <Collapse in={openMenu && menu.dashboard} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {/* <ListItem
              component="a"
              href="/dashboard/expedicao"
              className={`menu ${title === "Expedição" && "active"}`}
            >
              <ListItemIcon>
                <LocalShipping />
              </ListItemIcon>
              <ListItemText primary="Expedição" />
            </ListItem>
            <ListItem
              component="a"
              href="/dashboard/expedicao-v2"
              className={`menu ${title === "Expedição-v2" && "active"}`}
            >
              <ListItemIcon>
                <LocalShipping />
              </ListItemIcon>
              <ListItemText primary="Expedição-v2" />
            </ListItem> */}

                {can(8) && (
                  <ListItem
                    component="a"
                    href="/dashboard/carrinho-abandonado"
                    className={`menu ${title === "Carrinho Abandonado" && "active"}`}
                  >
                    <ListItemIcon>
                      <ShoppingBasketIcon />
                    </ListItemIcon>
                    <ListItemText primary="Carrinho Abandonado" />
                  </ListItem>
                )}

                {can(22) && (
                  <ListItem
                    component="a"
                    href="/dashboard/metas-gerais"
                    className={`menu ${title === "Metas Gerais" && "active"}`}
                  >
                    <ListItemIcon>
                      <ShowChart />
                    </ListItemIcon>
                    <ListItemText primary="Metas Gerais" />
                  </ListItem>
                )}

                {can(9) && (
                  <ListItem
                    component="a"
                    href="/dashboard/quantidade-de-pedidos"
                    className={`menu ${title === "Quantidade de pedidos" && "active"
                      }`}
                  >
                    <ListItemIcon>
                      <AddShoppingCartIcon />
                    </ListItemIcon>
                    <ListItemText primary="Quantidade de pedidos" />
                  </ListItem>
                )}

                {can(14) && (
                  <ListItem
                    component="a"
                    href="/dashboard/cascas"
                    className={`menu ${title === "Dashboard Cascas" && "active"}`}
                  >
                    <Box className={classes.iconPascoa}>
                      <img src={iconPascoa} alt="Pascoa" />
                    </Box>
                    <ListItemText primary="Cascas" />
                  </ListItem>
                )}

                {/*

          {/* <ListItem component="a" href="/dashboard/sac" className={`menu ${title === 'dashboard sac' && 'active'}`}>
            <Box className={classes.iconPascoa}>
              <ListItemIcon>
                <ContactsIcon />
              </ListItemIcon>
            </Box>
            <ListItemText primary="Dashboard sac" />
          </ListItem> */}
                {can(24) && (
                  <ListItem
                    component="a"
                    href="/dashboard/sac-v2"
                    className={`menu ${title === "dashboard sac-v2" && "active"}`}
                  >
                    <Box className={classes.iconPascoa}>
                      <ListItemIcon>
                        <ContactsIcon />
                      </ListItemIcon>
                    </Box>
                    <ListItemText primary="Dashboard sac-v2" />
                  </ListItem>
                )}

                {can(27) && (
                  <ListItem
                    component="a"
                    href="/dashboard/televendas"
                    className={`menu`}
                  >
                    <ListItemIcon>
                      <LineStyleRounded />
                    </ListItemIcon>
                    <ListItemText primary="Televendas" />
                  </ListItem>
                )}
              </List>
            </Collapse>
          </>
        )}

        {showLoja && (
          <>
            <ListItem
              button
              onClick={(e) => {
                setMenu({ loja: !menu.loja });
                if (!openMenu) setOpenMenu(!openMenu);
              }}
              className={`menu ${menu.loja ? "open" : ""} ${activeTop === "loja" ? "parent-active" : ""
                }`}
            >
              <ListItemIcon>
                <Storefront />
              </ListItemIcon>
              <ListItemText primary="Loja" />
              {menu.loja ? <ExpandLess /> : <ExpandMore />}
            </ListItem>

            <Collapse in={openMenu && menu.loja} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {can(41) && (
                  <ListItem
                    component="a"
                    href="/dashboard/ranking-caixas"
                    className={`menu ${title === "Ranking Caixas" && "active"}`}
                  >
                    <ListItemIcon>
                      <BarChart />
                    </ListItemIcon>
                    <ListItemText primary="Ranking" />
                  </ListItem>
                )}
                {can(42) && (
                  <ListItem
                    component="a"
                    href="/dashboard/produtividade"
                    className={`menu ${title === "Produtividade Caixas" && "active"}`}
                  >
                    <ListItemIcon>
                      <TimelineOutlined />
                    </ListItemIcon>
                    <ListItemText primary="Produtividade" />
                  </ListItem>
                )}
              </List>
            </Collapse>
          </>
        )}

        {showSite && (
          <>
            <ListItem
              button
              onClick={(e) => {
                setMenu({ site: !menu.site });
                if (!openMenu) setOpenMenu(!openMenu);
              }}
              className={`menu ${menu.site ? "open" : ""} ${activeTop === "site" ? "parent-active" : ""
                }`}
            >
              <ListItemIcon>
                <Public />
              </ListItemIcon>
              <ListItemText primary="Site" />
              {menu.site ? <ExpandLess /> : <ExpandMore />}
            </ListItem>

            <Collapse in={openMenu && menu.site} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {can(46) && (
                  <ListItem
                    component="a"
                    href="/dashboard/conciliacao-frete"
                    className={`menu ${title === "Conciliação de Frete" && "active"}`}
                  >
                    <ListItemIcon>
                      <Receipt />
                    </ListItemIcon>
                    <ListItemText primary="Conciliação de Frete" />
                  </ListItem>
                )}
                {can(39) && (
                  <ListItem
                    component="a"
                    href="/dashboard/ciclo"
                    className={`menu ${title === "Dashboard Ciclo" && "active"}`}
                  >
                    <ListItemIcon>
                      <TimelineOutlined />
                    </ListItemIcon>
                    <ListItemText primary="Dashboard Ciclo" />
                  </ListItem>
                )}
                {can(33) && (
                  <ListItem
                    component="a"
                    href="/dashboard/embarque"
                    className={`menu ${title === "Dashboard Embarque" && "active"}`}
                  >
                    <ListItemIcon>
                      <LocalShippingOutlined />
                    </ListItemIcon>
                    <ListItemText primary="Dashboard Embarque" />
                  </ListItem>
                )}
                {can(34) && (
                  <ListItem
                    component="a"
                    href="/dash/exp"
                    className={`menu ${title === "Dashboard Expedição" && "active"}`}
                  >
                    <ListItemIcon>
                      <LocalShipping />
                    </ListItemIcon>
                    <ListItemText primary="Dashboard Expedição" />
                  </ListItem>
                )}
                {can(45) && (
                  <ListItem
                    component="a"
                    href="/dashboard/tracking"
                    className={`menu ${title === "Dashboard TMS" && "active"}`}
                  >
                    <ListItemIcon>
                      <LocationOn />
                    </ListItemIcon>
                    <ListItemText primary="Dashboard TMS" />
                  </ListItem>
                )}
                {can(53) && (
                  <ListItem
                    component="a"
                    href="/dashboard/gerar-etiquetas"
                    className={`menu ${title === "Gerar Etiquetas" && "active"}`}
                  >
                    <ListItemIcon>
                      <Print />
                    </ListItemIcon>
                    <ListItemText primary="Gerar Etiquetas" />
                  </ListItem>
                )}
                {can(32) && (
                  <ListItem
                    component="a"
                    href="/dashboard/metas/expedicao"
                    className={`menu ${title === "Metas Expediçao" && "active"}`}
                  >
                    <ListItemIcon>
                      <ShowChart />
                    </ListItemIcon>
                    <ListItemText primary="Metas Expedição" />
                  </ListItem>
                )}
                {can(36) && (
                  <ListItem
                    component="a"
                    href="/dashboard/transportadoras"
                    className={`menu ${title === "Transportadoras" && "active"}`}
                  >
                    <ListItemIcon>
                      <Business />
                    </ListItemIcon>
                    <ListItemText primary="Transportadoras" />
                  </ListItem>
                )}

                {/* ── INTEGRAÇÃO ────────────────────────────────────── */}
                {can(55) && (
                  <>
                    <ListItem
                      button
                      onClick={(e) => {
                        setSubMenu({ ...subMenu, integracao: !subMenu.integracao });
                        if (!openSubMenu) setOpenSubMenu(!openSubMenu);
                      }}
                      className={`menu ${subMenu.integracao ? "open" : ""} ${activeSub === "integracao" ? "parent-active" : ""
                        }`}
                    >
                      <ListItemIcon>
                        <SwapHorizIcon />
                      </ListItemIcon>
                      <ListItemText primary="Integração" />
                      {subMenu.integracao ? <ExpandLess /> : <ExpandMore />}
                    </ListItem>

                    <Collapse in={openMenu && subMenu.integracao} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        <ListItem
                          component="a"
                          href="/integracao/embarque"
                          className={`menu ${title === "Integração Embarque" && "active"}`}
                        >
                          <ListItemIcon>
                            <LocalShippingOutlined />
                          </ListItemIcon>
                          <ListItemText primary="Embarque" />
                        </ListItem>
                      </List>
                    </Collapse>
                  </>
                )}

                {/* ── Ondas ─────────────────────────────────────── */}
                {can(60) && (
                  <>
                    <ListItem
                      button
                      onClick={(e) => {
                        setSubMenu({ ...subMenu, ondas: !subMenu.ondas });
                        if (!openSubMenu) setOpenSubMenu(!openSubMenu);
                      }}
                      className={`menu ${subMenu.ondas ? "open" : ""} ${activeSub === "ondas" ? "parent-active" : ""
                        }`}
                    >
                      <ListItemIcon>
                        <CallSplitIcon />
                      </ListItemIcon>
                      <ListItemText primary="Ondas" />
                      {subMenu.ondas ? <ExpandLess /> : <ExpandMore />}
                    </ListItem>

                    <Collapse in={openMenu && subMenu.ondas} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {can(60) && (
                          <ListItem
                            component="a"
                            href="/dashboard/ondas"
                            className={`menu ${title === "Ondas ao Vivo" && "active"}`}
                          >
                            <ListItemIcon>
                              <DashboardOutlined />
                            </ListItemIcon>
                            <ListItemText primary="Ondas ao Vivo" />
                          </ListItem>
                        )}
                      </List>
                    </Collapse>
                  </>
                )}
              </List>
            </Collapse>
          </>
        )}

        {showEbook && (
          <>
            <ListItem
              button
              onClick={(e) => {
                setMenu({ ebook: !menu.ebook });
                if (!openMenu) setOpenMenu(!openMenu);
              }}
              className={`menu ${menu.ebook ? "open" : ""} ${activeTop === "ebook" ? "parent-active" : ""
                }`}
            >
              <ListItemIcon>
                <BookmarkBorderIcon />
              </ListItemIcon>
              <ListItemText primary="Ebook" />
              {menu.ebook ? <ExpandLess /> : <ExpandMore />}
            </ListItem>

            <Collapse in={openMenu && menu.ebook} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {can(20) && (
                  <ListItem
                    component="a"
                    href="/ebook/novo"
                    className={`menu ${title === "Ebook" && "active"}`}
                  >
                    <ListItemIcon>
                      <BookIcon />
                    </ListItemIcon>
                    <ListItemText primary={"Ebook novo"} />
                  </ListItem>
                )}
                {can(21) && (
                  <ListItem
                    component="a"
                    href="/ebook/listar"
                    className={`menu ${title === "Listar ebook" && "active"}`}
                  >
                    <ListItemIcon>
                      <BookmarksIcon />
                    </ListItemIcon>
                    <ListItemText primary={"Listar ebook"} />
                  </ListItem>
                )}
              </List>
            </Collapse>
          </>
        )}

        {showEmbalei && (
          <>
            <ListItem
              button
              onClick={(e) => {
                setMenu({ embalei: !menu.embalei });
                if (!openMenu) setOpenMenu(!openMenu);
              }}
              className={`menu ${menu.embalei ? "open" : ""} ${activeTop === "embalei" ? "parent-active" : ""
                }`}
            >
              <ListItemIcon>
                <IconPackage />
              </ListItemIcon>
              <ListItemText primary="Embalei" />
              {menu.embalei ? <ExpandLess /> : <ExpandMore />}
            </ListItem>

            <Collapse in={openMenu && menu.embalei} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {can(29) && (
                  <ListItem
                    component="a"
                    href="/embalei/usuario"
                    className={`menu ${title === "Usuários Embalei" && "active"}`}
                  >
                    <ListItemIcon>
                      {" "}
                      <PeopleOutline />{" "}
                    </ListItemIcon>
                    <ListItemText primary={"Usuarios"} />
                  </ListItem>
                )}
                {can(17) && (
                  <ListItem
                    component="a"
                    href="/embalei/pesquisa"
                    className={`menu ${title === "Pesquisa Embalei" && "active"}`}
                  >
                    <ListItemIcon>
                      {" "}
                      <Search />{" "}
                    </ListItemIcon>
                    <ListItemText primary={"Pesquisar"} />
                  </ListItem>
                )}
                {can(18) && (
                  <ListItem
                    component="a"
                    href="/embalei/ranking"
                    className={`menu ${title === "Ranking Embalei" && "active"}`}
                  >
                    <ListItemIcon>
                      <BarChart />
                    </ListItemIcon>
                    <ListItemText primary="Ranking" />
                  </ListItem>
                )}

                {can(19) && (
                  <ListItem
                    component="a"
                    href="/embalei/meta"
                    className={`menu ${title === "Meta Embalei" && "active"}`}
                  >
                    <ListItemIcon>
                      <IconGoal />
                    </ListItemIcon>
                    <ListItemText primary="Meta" />
                  </ListItem>
                )}
              </List>
            </Collapse>
          </>
        )}

        {can(28) && (
          <ListItem component="a" href="/jornal-ofertas" className={`menu`}>
            <ListItemIcon>
              <Receipt />
            </ListItemIcon>
            <ListItemText primary="Jornal de Ofertas" />
          </ListItem>
        )}

        {can(26) && (
          <ListItem component="a" href="/retiraja" className={`menu`}>
            <ListItemIcon>
              <DoubleArrowOutlinedIcon />
            </ListItemIcon>
            <ListItemText primary="Retira Já" />
          </ListItem>
        )}

        {showSelos && (
          <>
            <ListItem
              button
              onClick={(e) => {
                setMenu({ seals: !menu.seals });
                if (!openMenu) setOpenMenu(!openMenu);
              }}
              className={`menu ${menu.seals ? "open" : ""} ${activeTop === "seals" ? "parent-active" : ""
                }`}
            >
              <ListItemIcon>
                <svg
                  width="23"
                  height="22"
                  viewBox="0 0 23 22"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M17.25 10.7492C17.25 11.9359 16.8981 13.0959 16.2388 14.0826C15.5795 15.0693 14.6425 15.8383 13.5461 16.2924C12.4497 16.7466 11.2433 16.8654 10.0795 16.6339C8.91557 16.4024 7.84647 15.8309 7.00736 14.9918C6.16824 14.1527 5.5968 13.0836 5.36529 11.9197C5.13378 10.7558 5.2526 9.54943 5.70672 8.45307C6.16085 7.35671 6.92988 6.41964 7.91658 5.76035C8.90327 5.10106 10.0633 4.74917 11.25 4.74917C12.8407 4.75099 14.3658 5.38372 15.4906 6.50854C16.6155 7.63336 17.2482 9.15843 17.25 10.7492ZM22.5 10.7492C22.4445 11.1713 22.3025 11.5774 22.0829 11.9422C21.8633 12.307 21.5708 12.6225 21.2238 12.8692C20.921 13.1123 20.6661 13.4098 20.4723 13.7462C20.4242 14.1516 20.4537 14.5623 20.5594 14.9566C20.6904 15.3508 20.7395 15.7676 20.7037 16.1814C20.668 16.5952 20.5481 16.9974 20.3514 17.3633C20.0622 17.6652 19.7144 17.9048 19.3292 18.0673C18.944 18.2299 18.5297 18.3119 18.1117 18.3085C17.7095 18.3289 17.3148 18.4254 16.9486 18.5927C16.6815 18.8864 16.472 19.2277 16.3308 19.5987C16.2047 20.0009 15.9973 20.3729 15.7214 20.6916C15.4455 21.0103 15.1071 21.2689 14.7271 21.4513C14.5559 21.5066 14.3771 21.5345 14.1972 21.5341C13.5564 21.4737 12.9398 21.2588 12.4002 20.9078C12.0503 20.6804 11.6597 20.5227 11.25 20.4435C10.8403 20.5227 10.4499 20.6804 10.1001 20.9079C9.76925 21.1515 9.39228 21.3254 8.99212 21.4187C8.59196 21.5121 8.17699 21.5231 7.77246 21.4509C7.39244 21.2685 7.05396 21.0099 6.77804 20.6912C6.50211 20.3725 6.29463 20.0005 6.16846 19.5983C6.02751 19.2273 5.81806 18.8862 5.55103 18.5927C5.18501 18.4253 4.7906 18.3289 4.38867 18.3085C3.97053 18.312 3.5561 18.2299 3.17086 18.0673C2.78563 17.9047 2.43773 17.665 2.14856 17.3629C1.9519 16.9971 1.83201 16.595 1.79624 16.1812C1.76047 15.7674 1.80958 15.3507 1.94055 14.9566C2.04622 14.5621 2.0758 14.1511 2.02771 13.7455C1.83353 13.4096 1.57888 13.1125 1.27661 12.8692C0.929456 12.6226 0.636901 12.3071 0.417241 11.9423C0.197581 11.5775 0.0555401 11.1713 0 10.7492C0.0555439 10.327 0.197548 9.92091 0.41714 9.55614C0.636731 9.19137 0.929191 8.87581 1.27624 8.62917C1.579 8.38599 1.83383 8.08857 2.02771 7.7521C2.0758 7.34679 2.04622 6.93602 1.94055 6.54177C1.80957 6.14758 1.76046 5.73078 1.79622 5.31694C1.83199 4.90309 1.95189 4.5009 2.14856 4.13503C2.43775 3.83313 2.78561 3.59355 3.17077 3.43101C3.55593 3.26847 3.97026 3.1864 4.3883 3.18985C4.79046 3.1694 5.18512 3.07298 5.5514 2.90567C5.81843 2.61193 6.02798 2.2707 6.16919 1.89969C6.29528 1.49748 6.50269 1.12548 6.77856 0.806774C7.05442 0.488072 7.39285 0.229474 7.77283 0.0470303C8.17727 -0.0248165 8.5921 -0.01369 8.99211 0.0797336C9.39211 0.173157 9.76895 0.346928 10.0997 0.590488C10.4497 0.817919 10.8402 0.975592 11.25 1.05484C11.6597 0.975641 12.0501 0.81796 12.3999 0.59048C12.7306 0.346436 13.1075 0.172404 13.5077 0.0790297C13.908 -0.014345 14.323 -0.0251008 14.7275 0.0474203C15.1076 0.229861 15.446 0.488454 15.722 0.807152C15.9979 1.12585 16.2054 1.49785 16.3315 1.90008C16.4725 2.27102 16.6819 2.61214 16.949 2.90567C17.315 3.07299 17.7094 3.16942 18.1113 3.18985C18.5295 3.18636 18.9439 3.26844 19.3291 3.43105C19.7144 3.59366 20.0623 3.83336 20.3514 4.13541C20.5481 4.50122 20.668 4.90334 20.7038 5.31713C20.7395 5.73092 20.6904 6.14765 20.5595 6.54178C20.4538 6.93627 20.4242 7.34728 20.4723 7.75284C20.6665 8.08877 20.9211 8.38589 21.2234 8.62918C21.5705 8.87575 21.8631 9.19128 22.0828 9.55606C22.3024 9.92083 22.4445 10.327 22.5 10.7492ZM18.75 10.7492C18.75 9.26581 18.3101 7.81576 17.486 6.58239C16.6619 5.34902 15.4906 4.38773 14.1201 3.82007C12.7497 3.25242 11.2417 3.10389 9.78682 3.39328C8.33197 3.68267 6.99559 4.39698 5.9467 5.44587C4.89781 6.49476 4.1835 7.83113 3.89411 9.28599C3.60472 10.7408 3.75325 12.2488 4.3209 13.6193C4.88856 14.9897 5.84985 16.1611 7.08322 16.9852C8.31659 17.8093 9.76664 18.2492 11.25 18.2492C13.2384 18.247 15.1448 17.4561 16.5509 16.05C17.9569 14.644 18.7478 12.7376 18.75 10.7492Z"
                    fill="white"
                  />
                </svg>
              </ListItemIcon>
              <ListItemText primary="Selos" />
              {menu.seals ? <ExpandLess /> : <ExpandMore />}
            </ListItem>

            <Collapse in={openMenu && menu.seals} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {can(30) && (
                  <ListItem
                    component="a"
                    href="/selos/adicionar"
                    className={`menu ${title === "Adicionar Selos" && "active"}`}
                  >
                    <ListItemIcon>
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M21 6C20.4 6 20 6.4 20 7V17C20 18.7 18.7 20 17 20H7C6.4 20 6 20.4 6 21C6 21.6 6.4 22 7 22H17C19.8 22 22 19.8 22 17V7C22 6.4 21.6 6 21 6ZM18 15V5C18 3.3 16.7 2 15 2H5C3.3 2 2 3.3 2 5V15C2 16.7 3.3 18 5 18H15C16.7 18 18 16.7 18 15ZM13 11H11V13C11 13.6 10.6 14 10 14C9.4 14 9 13.6 9 13V11H7C6.4 11 6 10.6 6 10C6 9.4 6.4 9 7 9H9V7C9 6.4 9.4 6 10 6C10.6 6 11 6.4 11 7V9H13C13.6 9 14 9.4 14 10C14 10.6 13.6 11 13 11Z"
                          fill="white"
                        />
                      </svg>
                    </ListItemIcon>
                    <ListItemText primary="Adicionar Selos" />
                  </ListItem>
                )}

                {can(31) && (
                  <ListItem
                    component="a"
                    href="/selos/ativar-desativar"
                    className={`menu ${title === "Ativar/Desativar Selos" && "active"
                      }`}
                  >
                    <ListItemIcon>
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M18 11H6C4.67392 11 3.40215 10.4732 2.46447 9.53553C1.52678 8.59785 1 7.32608 1 6C1 4.67392 1.52678 3.40215 2.46447 2.46447C3.40215 1.52678 4.67392 1 6 1H18C19.3261 1 20.5979 1.52678 21.5355 2.46447C22.4732 3.40215 23 4.67392 23 6C23 7.32608 22.4732 8.59785 21.5355 9.53553C20.5979 10.4732 19.3261 11 18 11ZM6 3C5.20435 3 4.44129 3.31607 3.87868 3.87868C3.31607 4.44129 3 5.20435 3 6C3 6.79565 3.31607 7.55871 3.87868 8.12132C4.44129 8.68393 5.20435 9 6 9H18C18.7956 9 19.5587 8.68393 20.1213 8.12132C20.6839 7.55871 21 6.79565 21 6C21 5.20435 20.6839 4.44129 20.1213 3.87868C19.5587 3.31607 18.7956 3 18 3H6Z"
                          fill="white"
                        />
                        <path
                          d="M18 23H6C4.67392 23 3.40215 22.4732 2.46447 21.5355C1.52678 20.5979 1 19.3261 1 18C1 16.6739 1.52678 15.4021 2.46447 14.4645C3.40215 13.5268 4.67392 13 6 13H18C19.3261 13 20.5979 13.5268 21.5355 14.4645C22.4732 15.4021 23 16.6739 23 18C23 19.3261 22.4732 20.5979 21.5355 21.5355C20.5979 22.4732 19.3261 23 18 23ZM6 15C5.20435 15 4.44129 15.3161 3.87868 15.8787C3.31607 16.4413 3 17.2044 3 18C3 18.7956 3.31607 19.5587 3.87868 20.1213C4.44129 20.6839 5.20435 21 6 21H18C18.7956 21 19.5587 20.6839 20.1213 20.1213C20.6839 19.5587 21 18.7956 21 18C21 17.2044 20.6839 16.4413 20.1213 15.8787C19.5587 15.3161 18.7956 15 18 15H6Z"
                          fill="white"
                        />
                        <path
                          d="M18 14H6C4.93913 14 3.92172 14.4214 3.17157 15.1716C2.42143 15.9217 2 16.9391 2 18C2 19.0609 2.42143 20.0783 3.17157 20.8284C3.92172 21.5786 4.93913 22 6 22H18C19.0609 22 20.0783 21.5786 20.8284 20.8284C21.5786 20.0783 22 19.0609 22 18C22 16.9391 21.5786 15.9217 20.8284 15.1716C20.0783 14.4214 19.0609 14 18 14ZM18 21C17.4067 21 16.8266 20.8241 16.3333 20.4944C15.8399 20.1648 15.4554 19.6962 15.2284 19.1481C15.0013 18.5999 14.9419 17.9967 15.0576 17.4147C15.1734 16.8328 15.4591 16.2982 15.8787 15.8787C16.2982 15.4591 16.8328 15.1734 17.4147 15.0576C17.9967 14.9419 18.5999 15.0013 19.1481 15.2284C19.6962 15.4554 20.1648 15.8399 20.4944 16.3333C20.8241 16.8266 21 17.4067 21 18C21 18.7956 20.6839 19.5587 20.1213 20.1213C19.5587 20.6839 18.7956 21 18 21Z"
                          fill="white"
                        />
                        <path
                          d="M6 11C5.0111 11 4.0444 10.7068 3.22215 10.1573C2.39991 9.60794 1.75904 8.82705 1.3806 7.91342C1.00217 6.99979 0.90315 5.99446 1.09608 5.02455C1.289 4.05465 1.76521 3.16373 2.46447 2.46447C3.16373 1.76521 4.05465 1.289 5.02455 1.09608C5.99446 0.90315 6.99979 1.00217 7.91342 1.3806C8.82705 1.75904 9.60794 2.39991 10.1573 3.22215C10.7068 4.0444 11 5.0111 11 6C11 7.32609 10.4732 8.59785 9.53554 9.53554C8.59785 10.4732 7.32609 11 6 11ZM6 3C5.40666 3 4.82664 3.17595 4.33329 3.50559C3.83994 3.83524 3.45543 4.30377 3.22836 4.85195C3.0013 5.40013 2.94189 6.00333 3.05765 6.58527C3.1734 7.16722 3.45912 7.70177 3.87868 8.12132C4.29824 8.54088 4.83279 8.8266 5.41473 8.94236C5.99667 9.05811 6.59987 8.9987 7.14805 8.77164C7.69623 8.54458 8.16477 8.16006 8.49441 7.66671C8.82406 7.17337 9 6.59335 9 6C9 5.20435 8.68393 4.44129 8.12132 3.87868C7.55871 3.31607 6.79565 3 6 3Z"
                          fill="white"
                        />
                        <path
                          d="M6 10C8.20914 10 10 8.20914 10 6C10 3.79086 8.20914 2 6 2C3.79086 2 2 3.79086 2 6C2 8.20914 3.79086 10 6 10Z"
                          fill="white"
                        />
                      </svg>
                    </ListItemIcon>
                    <ListItemText primary="Ativar/Desativar Selos" />
                  </ListItem>
                )}
              </List>
            </Collapse>
          </>
        )}

        {can(12) && (
          <ListItem
            component="a"
            href="/gerenciamento-sku"
            className={`menu ${title === "SKUs" && "active"}`}
          >
            <ListItemIcon>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 512 512"
                width="25"
                height="25"
                fill="#fff"
              >
                <g id="_06-cart" data-name="06-cart">
                  <g id="linear_color-2" data-name="linear color">
                    <path d="M480,6H32A26.03,26.03,0,0,0,6,32V384a26.03,26.03,0,0,0,26,26H208.671l-6.806,54.431a238.492,238.492,0,0,0-47.412,23.249A10,10,0,0,0,160,506H352a10,10,0,0,0,5.547-18.32,238.492,238.492,0,0,0-47.412-23.249L303.329,410H480a26.03,26.03,0,0,0,26-26V32A26.03,26.03,0,0,0,480,6ZM32,26H480a6.006,6.006,0,0,1,6,6V310H26V32A6.006,6.006,0,0,1,32,26ZM208,486h-6.526c15.893-6.393,35.309-12,54.526-12,19.148,0,38.556,5.607,54.459,12H208ZM289.2,458.16a134.521,134.521,0,0,0-66.39,0L228.827,410h54.346ZM486,384a6.006,6.006,0,0,1-6,6H32a6.006,6.006,0,0,1-6-6V330H486Z" />
                    <path d="M240,370h32a10,10,0,0,0,0-20H240a10,10,0,0,0,0,20Z" />
                    <path d="M104,74h24.792l45.721,137.162A9.969,9.969,0,0,0,176,214h0a26.036,26.036,0,1,0,13.831,4h83.462c-.188,1.976-.293,3.976-.293,6a63,63,0,1,0,63-63c-1.987,0-3.949.1-5.89.284l15.377-46.121A10,10,0,0,0,336,102H208a10,10,0,0,0,0,20H322.125l-9.348,28.039c-.257-.02-.515-.039-.777-.039H224a10,10,0,0,0,0,20h79.589a63.409,63.409,0,0,0-24.951,28h-87.43L145.487,60.838A10,10,0,0,0,136,54H104a10,10,0,0,0,0,20Zm72,172a6,6,0,1,1,6-6A6.006,6.006,0,0,1,176,246Zm203-22a43,43,0,1,1-43-43A43.049,43.049,0,0,1,379,224Z" />
                    <path d="M315.528,232.944l16,8a10,10,0,0,0,12.792-3.4l16-24a10,10,0,0,0-16.64-11.094l-11.1,16.655-8.1-4.052a10,10,0,0,0-8.944,17.888Z" />
                  </g>
                </g>
              </svg>
            </ListItemIcon>
            <ListItemText primary={"SKUs"} />
          </ListItem>
        )}

        <ListItem button onClick={signOutlogin} className={`menu`}>
          <ListItemIcon>
            <ExitToApp />
          </ListItemIcon>
          <ListItemText primary="Sair" />
        </ListItem>
      </List>
    </>
  );
}

function Dashboard(props) {
  const { children, title, fullscreen, styles } = props;

  const dispatch = useDispatch();

  const profile = useSelector((state) => state.user.profile);
  const userMenus = useMemo(() => normalizeUserMenus(profile), [profile]);
  const can = useCallback((id) => userMenus.has(Number(id)), [userMenus]);

  // Rota atual + seção ativa (para destacar o pai e auto-expandir)
  const path =
    typeof window !== "undefined" ? window.location.pathname : "";
  const active = useMemo(() => findActiveSection(path), [path]);

  const classes = useStyles();
  const [menu, setMenu] = useState(active.top ? { [active.top]: true } : {});
  const [subMenu, setSubMenu] = useState(
    active.sub ? { [active.sub]: true } : {}
  );
  const [openMenu, setOpenMenu] = useState(false);
  const [openSubMenu, setOpenSubMenu] = useState(false);

  function signOutlogin() {
    dispatch(signOut());
  }

  return (
    <div className={`${classes.root} ${styles}`}>
      <CssBaseline />
      <Hidden mdUp>
        <Drawer
          variant="temporary"
          anchor={"left"}
          open={openMenu}
          onClose={() => setOpenMenu(!openMenu)}
          className={clsx(classes.drawer, {
            [classes.drawerOpen]: openMenu,
            [classes.drawerClose]: !openMenu,
          })}
          classes={{
            paper: clsx({
              [classes.drawerOpen]: openMenu,
              [classes.drawerClose]: !openMenu,
            }),
          }}
        >
          <Menu
            menu={menu}
            subMenu={subMenu}
            setMenu={setMenu}
            setSubMenu={setSubMenu}
            openMenu={openMenu}
            openSubMenu={openSubMenu}
            setOpenMenu={setOpenMenu}
            setOpenSubMenu={setOpenSubMenu}
            classes={classes}
            title={title}
            signOutlogin={signOutlogin}
            can={can}
            activeTop={active.top}
            activeSub={active.sub}
          />
        </Drawer>
      </Hidden>

      <Hidden smDown>
        <Drawer
          style={{ display: fullscreen && "none" }}
          variant={"permanent"}
          className={clsx(classes.drawer, {
            [classes.drawerOpen]: openMenu,
            [classes.drawerClose]: !openMenu,
          })}
          classes={{
            paper: clsx({
              [classes.drawerOpen]: openMenu,
              [classes.drawerClose]: !openMenu,
            }),
          }}
        >
          <Menu
            menu={menu}
            subMenu={subMenu}
            setMenu={setMenu}
            setSubMenu={setSubMenu}
            openMenu={openMenu}
            openSubMenu={openSubMenu}
            setOpenMenu={setOpenMenu}
            setOpenSubMenu={setOpenSubMenu}
            classes={classes}
            title={title}
            signOutlogin={signOutlogin}
            can={can}
            activeTop={active.top}
            activeSub={active.sub}
          />
        </Drawer>
      </Hidden>

      <main className={`${classes.content} ${title === "Consulta de Margens" ? classes.mainMarginSimulator : ""}`}>
        <Hidden mdUp>
          <Box display="flex" justifyContent="flex-start" alignItems="center">
            <IconButton onClick={() => setOpenMenu(!openMenu)}>
              <Dehaze />
            </IconButton>
          </Box>
        </Hidden>
        {children}
      </main>
    </div>
  );
}

export default memo(Dashboard);
